<template>
  <div class="tiny" ref="root">
    <textarea ref="tinymce" :value="value"></textarea>
    <div class="_loader" v-show="isBusy">
      <md-progress-spinner :md-diameter="40" :md-stroke="4" md-mode="indeterminate" />
    </div>
    <md-snackbar md-position="center" :md-active.sync="isSnackbarOpen" md-persistent>
      <span>{{ snackbarMsg }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import $ from 'jquery';
import tinymce from 'tinymce/tinymce';
import { Storage } from 'aws-amplify';
import 'tinymce/plugins/textcolor';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/noneditable';
import 'tinymce/themes/modern/theme';
import 'tinymce/skins/lightgray/skin.min.css'
import { Auth } from 'aws-amplify';
import aws_exports from '@/aws-exports';
const AWS = require('aws-sdk');
const uuidv1 = require('uuid/v1');

export default {
  props: {
    value: {
      type: String,
      default: () => '',
    },
    mentionArr: {
      type: Array[Object],
      required: false,
      default: () => [],
    },
    plugins: {
      type: Array,
      required: false,
      default: () => ['textcolor lists advlist'],
    },
    toolbar: {
      type: String,
      required: false,
      default: () => 'formatselect | bold italic strikethrough forecolor backcolor | link | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | fontsizeselect | removeformat',
    },
    content: {
      type: String,
      required: false,
      default: () => '',
    },
    height: {
      type: Number,
      required: false,
      default: () => 300,
    },
    menubar: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    contentStyle: {
      type: String,
      required: false,
    },
    theId: {
      type: String,
      required: false,
    },
    isNote: {
      type: Boolean,
      required: false,
    },
    isComment: {
      type: Boolean,
      required: false,
    },
    attachments: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      isBusy: false,
      editor: undefined,
      autoComplete: undefined,
      isAutocompleteChange: false,
      timeoutId: null,
      isSnackbarOpen: false,
      snackbarMsg: '',
    };
  },
  
  beforeDestroy() {
    this.removeDragHandlers();
  },

  mounted() {
    
    this.addDragHandlers();
    this.initStorage();

    window.$bus.$on('refreshTinyMceEditor', (payload) => {
      if (payload.id == this.theId) {
        this.editor.setContent(payload.content);
        if (this.editor.selection) {
          this.editor.selection.collapse(false);
        } 
      }
    });

    window.$bus.$on('focusTinyMceEditor', (payload) => {
      if(payload.id == this.theId){
        this.editor.focus();
      }
    });

    tinymce.init({
      target: this.$refs.tinymce,
      height: this.height,
      menubar: this.menubar,
      plugins: this.plugins,
      toolbar: this.toolbar,
      content_style: '@import url(https://fonts.googleapis.com/css?family=Montserrat:500,700,500italic,700italic); body { font-family: Montserrat, sans-serif; font-size: 12px; }' + this.contentStyle,
      branding: false,
      setup: editor => {

        this.editor = editor;
        
        editor.on('paste', (e) => this.onPaste(e));
        
        editor.on("keypress", (e) => {

          if (!this.mentionArr.length) {
            return;
          }

          const autoCompleteData = { 
            delimiter: ['@'] 
          };
          const delimiterIndex = $.inArray(
            String.fromCharCode(e.which || e.keyCode),
            autoCompleteData.delimiter
          );

          if (delimiterIndex > -1 && this.prevCharIsSpace()) {
            if (
              this.autoComplete === undefined ||
              (this.autoComplete.hasFocus !== undefined && !this.autoComplete.hasFocus)
            ) {
              e.preventDefault();
              this.autoComplete = this.bootAutoComplete(
                editor,
                $.extend({}, autoCompleteData, {
                  delimiter: autoCompleteData.delimiter[delimiterIndex]
                })
              );
            }
          }
        });

        editor.on('init', (e) => {
          editor.focus();
          editor.setContent(this.content);
          editor.selection.select(editor.getBody(), true);
          editor.selection.collapse(false);
          this.$emit('input', this.content);
        });

        editor.on('KeyUp', (e) => {
          this.$emit('input', this.editor.getContent());
        });

        editor.on('Change', (e) => {
          this.$emit('input', this.editor.getContent());
        });

      }
    });
  },

  methods: {
    
    /**
     * Get an array of all drag events
     * @returns {Array} an array of event names
     */
    getDragEvents() {
      return [
        'drag',
        'dragstart',
        'dragend',
        'dragover',
        'dragenter',
        'dragleave',
        'drop',
      ];
    },
    
    /**
     * Get drag items from event
     * @param {Event} e the event object
     * @returns {Array} an array of attachment objects
     */
    getDragItems(e) {
      const items = this.$reduce(e, 'dataTransfer.items');
      if (!items || !items.length) {
        return [];
      }
      
      // prepare attachments
      const attachments = [];
      const description = '';
      const type = 'Other';
      
      // add attachments
      for (var i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          const { name } = file;
          attachments.push({ file, name, type, description });
        }
      }
      
      return attachments;
    },
    
    /**
     * Get target drag and drop element
     * @returns {HTMLElement}
     */
    getDragTargetEle() {
      return this.$refs.root;
    },
    
    /**
     * Prevent default browser behavior
     * @param {Event} e the event object
     * @returns {Void}
     */
    handleDragDefault(e) {
      e.preventDefault();
    },

    /**
     * Triggered when dragged over drag area
     * When new drag and drop event starts - add css class to help styling
     * @returns {Void}
     */
    onDragOver() {
      const ele = this.getDragTargetEle();
      if (ele) {
        clearTimeout(this.timeoutId);
        ele.classList.add('is-drag-target');
      }
    },

    /**
     * Triggered when user leaves drag area
     * Reset drag class using a timeout of 250ms 
     * Using timeout to stop CSS flash caused by simultaneous triiger of
     * both add and remove events
     * @returns {Void}
     */
    onDragLeave() {
      const ele = this.getDragTargetEle();
      if (ele) {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          ele.classList.remove('is-drag-target');
        }, 100);
      }
    },

    /**
     * Triggered when user drops new files into drop area
     * Resets css styling css class and emits droped files using event
     * @param {Event} e the event object
     * @returns {Void}
     */
    onDragDrop(e) {
      // reset drag helper css class
      clearTimeout(this.timeoutId);
      const ele = this.getDragTargetEle();
      ele.classList.remove('is-drag-target');
      
      // get items
      const items = this.getDragItems(e);
      if (!items.length) {
        return;
      }
      
      // notify
      const noun = items.length > 1 ? 'attachments' : 'attachment';
      this.snackbarMsg = `Added ${items.length} ${noun}`;
      this.isSnackbarOpen = true;
      
      // add attachments passed as props
      const attachments = this.attachments && this.attachments.length ? this.attachments : [];
      for (let i = 0; i < attachments.length; i++) {
        items.push(attachments[i]);
      }
      
      // emit event
      this.$emit('success', items);
    },
    
    /**
     * Add drag and drop event handlers
     * @returns {Void}
     */
    addDragHandlers() {
      const events = this.getDragEvents();
      const ele = this.getDragTargetEle();
      
      // tinymce ele not found
      if (!ele) {
        return;
      }
      
      // add event prevent default handlers for drag events
      for (let i = 0; i < events.length; i++) {
        ele.addEventListener(events[i], this.handleDragDefault);
      }
      
      // attach main drag event handlers
      ele.addEventListener('dragover', this.onDragOver);
      ele.addEventListener('dragleave', this.onDragLeave);
      ele.addEventListener('drop', this.onDragDrop);
    },
    
    /**
     * Removes all attached drag and drop event handlers
     * @returns {Void}
     */
    removeDragHandlers() {
      const ele = this.getDragTargetEle();
      const events = this.getDragEvents();
      for (let i = 0; i < events.length; i++) {
        ele.removeEventListener(events[i], this.handleDragDefault);
      }
      ele.removeEventListener('dragover', this.onDragOver);
      ele.removeEventListener('dragleave', this.onDragLeave);
      ele.removeEventListener('drop', this.onDragDrop);
    },

    prevCharIsSpace() {
      const start = this.editor.selection.getRng(true).startOffset;
      const text = this.editor.selection.getRng(true).startContainer.data || "";
      const charachter = text.substr(start - 1, 1);
      return !!$.trim(charachter).length ? false : true;
    },

    bootAutoComplete(ed, options) {

      // create new mentions list by removing _ from name
      const mentionArr = this.mentionArr.map(_item => {
        const item = Object.assign({}, _item);
        item.nameFormatted = item.name.replace(/_/ig, ' ');
        return item;
      });

      this.options = $.extend({}, {
          source: mentionArr,
          delay: 500,
          queryBy: "nameFormatted",
          valueBy: "name",
          items: 100 //items to load on @mention list
        },
        options
      );

      this.matcher = this.options.matcher || this.matcher;
      this.renderDropdown = this.options.renderDropdown || this.renderDropdown;
      this.render = this.options.render || this.render;
      this.insert = this.options.insert || this.insert;
      this.highlighter = this.options.highlighter || this.highlighter;

      this.query = "";
      this.hasFocus = true;

      this.renderInput();

      this.bindEvents();
    },

    renderInput() {
      const rawHtml = `
        <span id="autocomplete">
          <span id="autocomplete-delimiter" style="margin-right: -5px;">${this.options.delimiter}</span>
          <span id="autocomplete-searchtext"><span class="dummy">\uFEFF</span></span>
        </span>
      `;
      this.editor.execCommand("mceInsertContent", false, rawHtml);
      this.editor.focus();
      this.editor.selection.select(
        this.editor.selection.dom.select("span#autocomplete-searchtext span")[0]
      );
      this.editor.selection.collapse(0);
    },

    bindEvents() {
      this.editor.on(
        "keyup",
        (this.editorKeyUpProxy = this.rteKeyUp),
      );
      this.editor.on(
        "keydown",
        (this.editorKeyDownProxy = this.rteKeyDown),
        true
      );
      this.editor.on(
        "click",
        (this.editorClickProxy = this.rteClicked),
      );
      $("body").on(
        "click",
        (this.bodyClickProxy = this.rteLostFocus),
      );
      $(this.editor.getWin()).on(
        "scroll",
        (this.rteScroll = $.proxy(function() {
          this.cleanUp(true);
        }, this))
      );
    },

    unbindEvents() {
      this.editor.off("keyup", this.editorKeyUpProxy);
      this.editor.off("keydown", this.editorKeyDownProxy);
      this.editor.off("click", this.editorClickProxy);

      $("body").off("click", this.bodyClickProxy);

      $(this.editor.getWin()).off("scroll", this.rteScroll);
    },

    rteKeyUp(e) {
      switch (e.which || e.keyCode) {
        //DOWN ARROW
        case 40:
        //UP ARROW
        case 38:
        //SHIFT
        case 16:
        //CTRL
        case 17:
        //ALT
        case 18:
          break;

        //BACKSPACE
        case 8:
          if (this.query === "") {
            this.cleanUp(true);
          } else {
            this.lookup();
          }
          break;
        
        case 50:
          if (this.query.trim() === '@') {
            this.query = '';
            this.cleanUp(true);
          } else {
            this.lookup();
          }
          break;

        //TAB
        case 9:
        //ENTER
        case 13:
          var item =
            this.$dropdown !== undefined
              ? this.$dropdown.find("li.active")
              : [];
          if (item.length) {
            this.select(item.data());
            this.cleanUp(false);
          } else {
            this.cleanUp(true);
          }
          break;

        //ESC
        case 27:
          this.cleanUp(true);
          break;

        // SPACE
        case 32:
          this.cleanUpOnBackspace();
          break;

        default:
          this.lookup();
      }
    },

    rteKeyDown(e) {
      switch (e.which || e.keyCode) {
        //TAB
        case 9:
        //ENTER
        case 13:
        //ESC
        case 27:
          e.preventDefault();
          break;

        //UP ARROW
        case 38:
          e.preventDefault();
          if (this.$dropdown !== undefined) {
            this.highlightPreviousResult();
          }
          break;
        //DOWN ARROW
        case 40:
          e.preventDefault();
          if (this.$dropdown !== undefined) {
            this.highlightNextResult();
          }
          break;
      }

      e.stopPropagation();
    },

    rteClicked(e) {
      var $target = $(e.target);

      if (
        this.hasFocus &&
        $target.parent().attr("id") !== "autocomplete-searchtext"
      ) {
        this.cleanUp(true);
      }
    },

    rteLostFocus() {
      if (this.hasFocus) {
        this.cleanUp(true);
      }
    },

    lookup() {
      this.query = $.trim(
        $(this.editor.getBody())
          .find("#autocomplete-searchtext")
          .text()
      ).replace("\ufeff", "");

      if (this.$dropdown === undefined) {
        this.show();
      }

      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(
        $.proxy(function() {
          // Added delimiter parameter as last argument for backwards compatibility.
          var items = $.isFunction(this.options.source)
            ? this.options.source(
                this.query,
                $.proxy(this.process, this),
                this.options.delimiter
              )
            : this.options.source;
          if (items) {
            this.process(items);
          }
        }, this),
        this.options.delay
      );
    },

    matcher(item) {
      return ~item[this.options.queryBy]
        .toLowerCase()
        .indexOf(this.query.toLowerCase());
    },

    sorter(items) {
      var beginswith = [],
        caseSensitive = [],
        caseInsensitive = [],
        item;

      while ((item = items.shift()) !== undefined) {
        if (
          !item[this.options.queryBy]
            .toLowerCase()
            .indexOf(this.query.toLowerCase())
        ) {
          beginswith.push(item);
        } else if (~item[this.options.queryBy].indexOf(this.query)) {
          caseSensitive.push(item);
        } else {
          caseInsensitive.push(item);
        }
      }

      return beginswith.concat(caseSensitive, caseInsensitive);
    },

    highlighter(text) {
      return text.replace(
        new RegExp(
          "(" + this.query.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1") + ")",
          "ig"
        ),
        function($1, match) {
          return "<strong>" + match + "</strong>";
        }
      );
    },

    show() {
      var offset = this.editor.inline ? this.offsetInline() : this.offset();

      this.$dropdown = $(this.renderDropdown()).css({
        top: offset.top,
        left: offset.left
      });

      $("body").append(this.$dropdown);

      this.$dropdown.on("click", $.proxy(this.autoCompleteClick, this));
    },

    process(data) {
      if (!this.hasFocus) {
        return;
      }

      var _this = this,
        result = [],
        items = $.grep(data, function(item) {
          return _this.matcher(item);
        });

      items = _this.sorter(items);

      console.log(items);

      items = items.slice(0, this.options.items);

      $.each(items, function(i, item) {
        var $element = $(_this.render(item));

        $element.html(
          $element
            .html()
            .replace($element.text(), _this.highlighter($element.text()))
        );

        $.each(items[i], function(key, val) {
          $element.attr("data-" + key, val);
        });

        result.push($element[0].outerHTML);
      });

      if (result.length) {
        this.$dropdown.html(result.join("")).show();
      } else {
        this.$dropdown.hide();
      }
    },

    renderDropdown() {
      return '<ul class="rte-autocomplete dropdown-menu"><li class="loading"></li></ul>';
    },

    render(item) {
      return (
        "<li>" +
        '<a href="javascript:;"><span>' +
        item[this.options.queryBy] +
        "</span></a>" +
        "</li>"
      );
    },

    autoCompleteClick(e) {
      var item = $(e.target)
        .closest("li")
        .data();
      if (!$.isEmptyObject(item)) {
        this.$emit('itemSelected', item);
        this.select(item);
        this.cleanUp(false);
      }
      e.stopPropagation();
      e.preventDefault();
    },

    highlightPreviousResult() {
      var currentIndex = this.$dropdown.find("li.active").index(),
        index =
          currentIndex === 0
            ? this.$dropdown.find("li").length - 1
            : --currentIndex;

      this.$dropdown
        .find("li")
        .removeClass("active")
        .eq(index)
        .addClass("active");
    },

    highlightNextResult() {
      var currentIndex = this.$dropdown.find("li.active").index(),
        index =
          currentIndex === this.$dropdown.find("li").length - 1
            ? 0
            : ++currentIndex;

      this.$dropdown
        .find("li")
        .removeClass("active")
        .eq(index)
        .addClass("active");
    },

    select(item) {
      this.editor.focus();
      var selection = this.editor.dom.select("span#autocomplete")[0];
      this.editor.dom.remove(selection);
      console.log(item);
      // this.editor.execCommand("mceInsertContent", false, '@');
      // this.editor.execCommand("mceInsertContent", false, this.insert(item));
      this.insertMentionsBlock(item);
    },
    
    insertMentionsBlock(item) {
      const value = item[this.options.valueBy];
      const node = document.createElement('input');
      const nodeWidth = this.getTextNodeWidth(value) + 5;
      node.setAttribute('value', item['nameformatted']);
      node.setAttribute('id', value);
      node.setAttribute('readonly', true);
      node.setAttribute('disabled', true);
      node.setAttribute('size', value.length);
      node.setAttribute('user', JSON.stringify(item));
      node.setAttribute('style', `
        width: ${nodeWidth}px;
        margin: 0.25rem 0;
        padding: 0.25em 0.5em;
        background: dodgerblue;
        border-radius: 99em;
        border: none;
        vertical-align: middle;
        color: #fff;
        cursor: pointer;
        font-family: arial, sans-serif;
        font-size: 13px;
        box-sizing: content-box;
        -webkit-box-sizing: content-box;
      `);
      const range = this.editor.selection.getRng();
      range.insertNode(node);
      range.collapse();
      this.editor.execCommand("mceInsertContent", false, '&nbsp;');
    },
    
    getTextNodeWidth(str) {
      const node = document.createElement('span');
      node.innerText = str;
      node.setAttribute('style', `
        visiblity: hidden;
        postition: absolute;
        font-family: arial, sans-serif;
        font-size: 13px;
        text-indent: -999;
        box-sizing: border-box;
        -webkit-box-sizing: border-box;
      `);
      document.body.append(node);
      const width = node.getBoundingClientRect().width;
      node.remove();
      return width;
    },

    insert(item) {
      return "<span>" + item[this.options.queryBy] + "</span>&nbsp;";
    },

    cleanUp(rollback, insertSpace) {
      this.unbindEvents();
      this.hasFocus = false;

      if (this.$dropdown !== undefined) {
        this.$dropdown.remove();
        delete this.$dropdown;
      }

      if (rollback) {
        this.isAutocompleteChange = true;
        var text = this.query,
          $selection = $(this.editor.dom.select("span#autocomplete")),
          replacement = $("<p>" + this.options.delimiter + text + "</p>")[0]
            .firstChild,
          focus =
            $(this.editor.selection.getNode()).offset().top ===
            $selection.offset().top +
              ($selection.outerHeight() - $selection.height()) / 2;

        let replacementElement = document.createElement('br');
        const editorText = this.editor.getContent({ format: 'text' }).trim();
        if (editorText.length) {
          replacementElement = replacement;
        }

        this.editor.dom.replace(replacementElement, $selection[0]);

        if (focus) {
          this.editor.selection.select(replacement);
          this.editor.selection.collapse();
          if (insertSpace) {
            this.editor.execCommand('mceInsertContent', false, '<span>&nbsp;</span>')
          }
        }

        setTimeout(() => {
          this.isAutocompleteChange = false;
        }, 50);
      }
    },

    cleanUpOnBackspace() {
      const text = this.query.trim();
      if (!text.length) {
        this.cleanUp(true, true);
      }
    },

    offset() {
      var rtePosition = $(this.editor.getContainer()).offset(),
        contentAreaPosition = $(
          this.editor.getContentAreaContainer()
        ).position(),
        nodePosition = $(
          this.editor.dom.select("span#autocomplete")
        ).position();

      return {
        top:
          rtePosition.top +
          contentAreaPosition.top +
          nodePosition.top +
          $(this.editor.selection.getNode()).innerHeight() -
          $(this.editor.getDoc()).scrollTop() +
          5,
        left: rtePosition.left + contentAreaPosition.left + nodePosition.left
      };
    },

    offsetInline() {
      var nodePosition = $(
        this.editor.dom.select("span#autocomplete")
      ).offset();

      return {
        top:
          nodePosition.top +
          $(this.editor.selection.getNode()).innerHeight() +
          5,
        left: nodePosition.left
      };
    },
    
    /**
     * Handle paste event - if HTML was copied then paste as it is
     * If Image Blob was copied then convert it into img tag
     * @param {paste event} e
     * @returns {void}
     */
    async onPaste(e) {
      if (!e.clipboardData || this.isBusy) {
        return;
      }
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text && !e.clipboardData.items.length) {
        return;
      }
      if (text) {
        return this.convertLinkToHref(e);
      } else {
        await this.convertImageToInlineImage(e);
      }
    },
    
    /**
     * Convert all links in text to link tags 
     * @see https://github.com/SoapBox/linkifyjs
     * Insert converted HTML to current cursor position
     * @param {paste event} e
     * @returns {void} 
     */
    convertLinkToHref(e) {
      let html = e.clipboardData.getData('text/html');
      if (!html) {
        html = e.clipboardData.getData('text/plain');
      }
      // html = linkifyHtml(html, { defaultProtocol: 'http' });
      const node = document.createElement('span');
      const range = this.editor.selection.getRng();
      node.innerHTML = html;
      range.insertNode(node);
      range.collapse();
      this.editor.execCommand("mceInsertContent", false, '');
    },
    
    /**
     * Read clipboardData and convert all images to inline images
     * @todo - maybe store images on AWS S3 bucket and use links instead 
     * @see https://developer.mozilla.org/en-US/docs/Web/API/ClipboardEvent/clipboardData
     * @see https://developer.mozilla.org/en-US/docs/Web/API/FileReader
     * @returns {void}
     */
    async convertImageToInlineImage(e) {
      const data  = e.clipboardData;
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];
        const imageFile = item.getAsFile();
        
        if ('file' != item.kind || -1 == ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'].indexOf(item.type)) {
          continue;
        }
        
        const dir = 'public/images';
        if (this.isNote) {
          dir += '/notes';
        } 
        if (this.isComment) {
          dir += '/comments';
        }
        
        const attachmentUuid = uuidv1();
        const amplifyEnv = aws_exports.aws_content_delivery_bucket.match(/.*-(\w+)/)[1]
        const imagesBucket = 'atlasemailattachments-' + amplifyEnv 
        const fileKey = `${dir}/${attachmentUuid}/${imageFile.name}`;
        
        this.isBusy = true;
        
        Auth.currentAuthenticatedUser()
          .then(user => {
            Auth.currentCredentials()
              .then(credentials => {
                
                const S3 = new AWS.S3({
                  apiVersion: '2006-03-01',
                  credentials: Auth.essentialCredentials(credentials),
                  region: aws_exports.aws_appsync_region
                });
                
                const params = {
                  Bucket: imagesBucket,
                  Key: fileKey,
                  Body: imageFile,
                  ACL: "public-read"
                };
                
                S3.putObject(params)
                  .promise()
                  .then(data => {
                    const url = `https://${imagesBucket}.s3.amazonaws.com/${fileKey}`
                    const img = document.createElement("IMG");
                    img.setAttribute('style', 'width: auto; max-width: 100%; vertical-align: middle;');
                    img.src = url;
                    img.setAttribute('width', 256);
                    img.onload = (e) => {
                      img.setAttribute('width', Math.min(img.width, 256));
                      this.isBusy = false;
                    }

                    const range = this.editor.selection.getRng();
                    range.insertNode(img);
                    range.collapse();
                    
                    this.editor.execCommand("mceInsertContent", false, '');
                  })
                  .catch(err => {
                    console.log('Error uploading image to S3 Bucket', err);
                    this.isBusy = false;
                  });
              })
              .catch(err => {
                console.log('Error uploading image to S3 Bucket', err);
                this.isBusy = false;
              });
          })
          .catch(err => {
            console.log('Error uploading image to S3 Bucket', err);
            this.isBusy = false;
          });    
      }
    },
    
    initStorage() {
      const amplifyEnv = aws_exports.aws_content_delivery_bucket.match(/.*-(\w+)/)[1]
      const imagesBucket = 'atlasemailattachments-' + amplifyEnv        
      Storage.configure({
        AWSS3: {
          bucket: imagesBucket,
          region: aws_exports.aws_project_region
        }
      });
    }
    
  },

  watch: {
    value: function(newValue, oldValue) {
      if (this.isAutocompleteChange) {
        return;
      }
      if (this.value !== this.editor.getContent()) {
        this.editor.focus();
        this.editor.setContent(this.value);
        if (this.editor.selection) {
          this.editor.selection.collapse(false);
        } 
      }
    }
  }

};
</script>

<style lang="css" scoped>
  ._loader {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
</style>
